"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { useEffect, useState, useCallback } from "react";
import { getSupabaseClient } from "@/lib/supabaseClient";
import { useRouter } from "next/navigation";
import { EB_Garamond } from "next/font/google";
import { FileText, Layers, Share2, Shield } from "lucide-react";

const display = EB_Garamond({ subsets: ["latin"], weight: ["400", "600", "700"] });

export default function HomePage() {
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const router = useRouter();

  useEffect(() => {
    const supabase = getSupabaseClient();
    supabase.auth.getSession().then(({ data }) => setLoggedIn(!!data.session));
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      setLoggedIn(!!session);
    });
    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const handleLogout = useCallback(async () => {
    const supabase = getSupabaseClient();
    try {
      await supabase.auth.signOut();
    } finally {
      setLoggedIn(false);
      router.replace("/");
    }
  }, [router]);

  return (
    <main className="flex-1">
      {/* Header */}
      <header className="w-full border-b border-[#EDE7E1] bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="text-[13px] tracking-[.18em] uppercase text-[#B0654A]">Analytia</div>
          <nav className="hidden md:flex gap-6 text-sm text-[#59656E]">
            <a href="#features" className="hover:underline underline-offset-4">Características</a>
            <a href="#how" className="hover:underline underline-offset-4">Método</a>
            <a href="https://engramapsicologia.es/quienes-somos/" target="_blank" rel="noopener noreferrer" className="hover:underline underline-offset-4">Quienes somos</a>
            {loggedIn ? (
              <div className="flex items-center gap-3">
                <Link href="/app" className="hover:underline underline-offset-4">Ir a la app</Link>
                <button onClick={handleLogout} className="text-[#443B33]/80 hover:text-[#443B33]">Cerrar sesión</button>
              </div>
            ) : (
              <Link href="/login" className="hover:underline underline-offset-4">Login</Link>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* decorative background */}
        <div className="pointer-events-none absolute inset-0 opacity-30">
          <div className="absolute -top-24 left-1/2 h-[420px] w-[840px] -translate-x-1/2 rounded-full bg-[radial-gradient(60%_60%_at_50%_30%,#ECE3D5,transparent)]" />
        </div>
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="mb-3 text-[12px] tracking-[.18em] uppercase text-[#B0654A]">Con rigor científico</p>
          <h1 className={`${display.className} text-4xl md:text-5xl font-semibold tracking-tight text-[#443B33]`}>
            Análisis Funcional de la Conducta, claro y estructurado.
          </h1>
          <p className="text-[17px] md:text-[18px] text-[#6B625B] mt-4 max-w-2xl mx-auto">
          Extrae conductas, modela el ABC y formula hipótesis con ayuda de IA, de forma rigurosa y guiada.
          </p>

          {/* Equation / model callout */}
          <div className="mt-8 flex items-center justify-center">
            <div className="rounded-md border border-[#E5DED6] bg-[#FAF4EB] px-4 py-3 text-sm text-[#443B33]/90">
              <span className="mr-3 text-[11px] tracking-[.16em] uppercase text-[#8C9280]">Eq. 1 — Modelo ABC</span>
              <span className={`${display.className} italic`}>
                F[ Conducta | Antecedentes, Consecuencias ] ⇒ Hipótesis
              </span>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-center gap-3">
            {loggedIn ? (
              <>
                <Link href="/app">
                  <Button size="lg" className="bg-[#59656E] hover:bg-[#4d585f] text-white border border-[#4d585f]">Ir a la app</Button>
                </Link>
                <Button variant="outline" size="lg" onClick={handleLogout} className="border-[#E5DED6] text-[#443B33]">Cerrar sesión</Button>
              </>
            ) : (
              <Link href="/login">
                <Button size="lg" className="bg-[#59656E] hover:bg-[#4d585f] text-white border border-[#4d585f]">Continuar con Google</Button>
              </Link>
            )}
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSd8No3k_4L_lAYmBbYR4U1nWMLnpVeFPkkYvm4HyicfBL5BYQ/viewform"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#59656E] hover:underline underline-offset-4"
            >
              Danos tu opinión →
            </a>
          </div>
        </div>
      </section>


      
      {/* Features */}
      <section id="features" className="container mx-auto px-4 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[{
            overline: 'EXTRACCIÓN',
            title: 'Asistida por IA',
            body: 'Sube tus notas o PDFs y deja que el sistema identifique las conductas relevantes.',
            Icon: FileText
          },{
            overline: 'ANÁLISIS',
            title: 'Análisis funcional estructurado',
            body: 'Organiza antecedentes, conductas, consecuencias e hipótesis de forma clara y estructurada.',
            Icon: Layers
          },{
            overline: 'VISUALIZACIÓN',
            title: 'Visualización profesional',
            body: 'Diagramas claros para informes, devoluciones, docencia y supervisión.',
            Icon: Share2
          },{
            overline: 'SEGURIDAD',
            title: 'Datos protegidos',
            body: 'Los datos solo se almacenaran en tu navegador, no se usan para entrenar modelos y no se transfieren entre dispositivos.',
            Icon: Shield
          }].map(({overline, title, body, Icon}, idx) => (
            <div key={idx} className="rounded-xl border border-[#E5DED6] bg-[#ECE3D5]/50 p-6 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-sm">
              <p className="text-[11px] tracking-[.16em] uppercase text-[#B0654A] mb-2">{overline}</p>
              <div className="flex items-start gap-3">
                <Icon className="h-5 w-5 text-[#59656E]" />
                <div>
                  <h3 className={`${display.className} text-[18px] font-semibold text-[#443B33]`}>{title}</h3>
                  <p className="text-sm text-[#6B625B] mt-1">{body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>


      {/* Method / How it works */}
      <section id="how" className="container mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {["Subir notas","Identificar conductas","Analizar relaciones funcionales","Visualizar e informar"].map((step, i) => (
            <div key={i} className="rounded-xl border border-[#E5DED6] bg-white p-6">
              <div className="flex items-center gap-4">
                <div className="h-9 w-9 rounded-full border border-[#8C9280] text-[#59656E] grid place-items-center font-medium" style={{fontFeatureSettings: '"tnum" 1'}}>
                  {i+1}
                </div>
                <div className="font-medium text-[#443B33]">{step}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tutorial Video */}
      <section className="container mx-auto px-4 py-1">
        <div className="text-center mb-5">
          <p className="mb-3 text-[12px] tracking-[.18em] uppercase text-[#B0654A]">Cómo funciona</p>
          <h2 className={`${display.className} text-3xl md:text-4xl font-semibold tracking-tight text-[#443B33]`}>
            Mira Analytia en acción
          </h2>
          <p className="text-[16px] text-[#6B625B] mt-3 max-w-xl mx-auto">
            Tutorial rápido para empezar a usar la aplicación
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <div className="rounded-xl border border-[#E5DED6] bg-white p-4 shadow-sm">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src="https://player.vimeo.com/video/1125933633?h=c49fba6a0a&badge=0&autopause=0&player_id=0&app_id=58479"
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Tutorial de Analytia"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-1">
        {/*empty space*/}
        <div className="h-20"></div>
      </section>
      {/* Trust */}
      {/* <section className="container mx-auto px-4 pb-6">
        <div className="rounded-xl border border-[#E5DED6] bg-[#FAF4EB] p-8 text-center">
          <p className={`${display.className} text-[15px] text-[#443B33]`}>
            “Analytia me ahorra horas de formulación cada semana y me ayuda a mantener rigor clínico.” — Psicóloga Clínica, Barcelona
          </p>
        </div>
      </section> */}

      {/* Footer */}
      <footer className="border-t border-[#EDE7E1]">
        <div className="container mx-auto px-4 py-6 text-sm text-[#6B625B] flex items-center justify-between">
          <div>© {new Date().getFullYear()} Analytia</div>
          <div className="flex gap-4">
            <a href="https://engramapsicologia.es/quienes-somos/" target="_blank" rel="noopener noreferrer" className="hover:underline underline-offset-4">Acerca de</a>
            <a href="https://engramapsicologia.es/privacidad/" target="_blank" rel="noopener noreferrer" className="hover:underline underline-offset-4">Privacidad</a>
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSd8No3k_4L_lAYmBbYR4U1nWMLnpVeFPkkYvm4HyicfBL5BYQ/viewform"
              className="hover:underline underline-offset-4"
            >
              Contacto
            </a>
          </div>
        </div>
      </footer>
    </main>
  );
}

